import { useState, useEffect, useRef } from 'react';

/**
 * Shared async data hook for live API panels.
 * Returns { data, loading, error, retry }. Refreshes in-place —
 * never re-triggers the full loading skeleton on background refreshes.
 *
 * Usage:
 *   const { data, loading, error, retry } =
 *     useAsyncData('/api/agents', []);
 */
function useAsyncData(endpoint, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const hasLoadedRef = useRef(false);

  const load = () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Only show loading spinner on the very first fetch.
    // Subsequent background refreshes update data in-place.
    if (!hasLoadedRef.current) {
      setLoading(true);
      setError(null);
    }

    fetch(endpoint, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        return res.json();
      })
      .then((json) => {
        setData(json);
        hasLoadedRef.current = true;
        setLoading(false);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') setError(err.message);
        if (!hasLoadedRef.current) {
          setLoading(false);
        }
      });
  };

  useEffect(() => {
    load();

    return () => { abortRef.current?.abort(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, ...deps]);

  return { data, loading, error, retry: load };
}

export default useAsyncData;
export { useAsyncData };
